import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Award, Loader2, AlertCircle, ScrollText } from "lucide-react";
import { createCheckout } from "../lib/api";
import { Tx } from "../context/AutoTranslateContext";

const CERTS = [
  {
    key: "juz_certificate",
    price: 8,
    title: "Juz' Completion Certificate",
    body: "Issued after passing a live 30-min recitation review of the completed Juz' with an approved tutor. Signed & stamped PDF delivered by email.",
    perWhat: "one 30-min review + one signed certificate",
    icon: ScrollText,
  },
  {
    key: "ijaza_certificate",
    price: 80,
    title: "Full Qur'an / Ijāza Certificate",
    body: "The academy's highest honour — issued after successful completion & assessment of the full 30 Juz'. Includes the ijāza chain (sanad) of the reviewing sheikh.",
    perWhat: "full 30-Juz' assessment + sanad-ijāza PDF",
    icon: Award,
  },
];

const Certificates = () => {
  const [pick, setPick] = useState(null);         // certificate key
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const buy = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await createCheckout({
        product_type: pick,
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div data-testid="certificates-page" className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link><span>/</span>
        <span className="text-forest"><Tx>Certificates</Tx></span>
      </div>

      <div className="text-center mb-10">
        <Award className="w-11 h-11 text-gold mx-auto mb-3" />
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2"><Tx>Recognition of progress</Tx></p>
        <h1 className="font-heading text-4xl sm:text-5xl text-forest font-bold mb-3">
          <Tx>Completion & Ijāza Certificates</Tx>
        </h1>
        <p className="text-sm text-mute max-w-lg mx-auto">
          <Tx>Every certificate is issued only after a live 30-min review with an approved qārī — never automatically.</Tx>
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-8">
        {CERTS.map((c) => {
          const Icon = c.icon;
          const selected = pick === c.key;
          return (
            <button
              key={c.key} type="button"
              data-testid={`cert-pick-${c.key}`}
              onClick={() => setPick(c.key)}
              className={`text-left rounded-3xl p-6 border-2 transition ${
                selected ? "border-gold bg-gold/10" : "border-forest/10 bg-cream/60 hover:border-forest/30"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <Icon className="w-8 h-8 text-gold" />
                <div className="text-right">
                  <p className="font-heading text-3xl text-forest font-bold leading-none">£{c.price}</p>
                  <p className="text-[10px] text-mute mt-1"><Tx>one-time payment</Tx></p>
                </div>
              </div>
              <p className="font-heading text-lg text-forest font-semibold mb-2"><Tx>{c.title}</Tx></p>
              <p className="text-xs text-mute leading-relaxed mb-3"><Tx>{c.body}</Tx></p>
              <p className="text-[11px] text-gold font-medium">
                <Tx>Includes:</Tx> <Tx>{c.perWhat}</Tx>
              </p>
            </button>
          );
        })}
      </div>

      {pick && (
        <form onSubmit={buy} className="clay-surface rounded-3xl p-6 space-y-4" data-testid="cert-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Your full name (as it will appear on the certificate)</Tx></span>
              <input data-testid="cert-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)}
                className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Email</Tx></span>
              <input data-testid="cert-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>WhatsApp phone (with country code — we'll message you to schedule the review)</Tx></span>
              <input data-testid="cert-phone" type="tel" required minLength={6} placeholder="+44 7445 372190"
                value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
            </label>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
            </div>
          )}

          <button data-testid="cert-submit" type="submit" disabled={busy}
            className="w-full py-3 bg-forest text-sand hover:bg-sage rounded-full font-medium disabled:opacity-60 transition">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
              <><Tx>Pay £</Tx>{CERTS.find(c => c.key === pick)?.price} <Tx>& request my certificate</Tx></>
            )}
          </button>
          <p className="text-[11px] text-mute italic text-center">
            <Tx>After payment, our team will contact you within 3 working days to schedule your review.</Tx>
          </p>
        </form>
      )}
    </div>
  );
};

export default Certificates;
